// src/routes/merchants/useMerchantSharing.ts
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import { MerchantsError } from './useMerchants'

/**
 * `POST /merchants/{id}/share {household_id}` (backend #467): share a merchant the user owns with their
 * household. Owner only, into a household they are an active member of; idempotent.
 *
 * Rejects with a `MerchantsError` carrying the status, for the caller's copy: 403 is a non-owner or an
 * inactive membership, 409 is a merchant that cannot be shared there (global, shared with another
 * household, or the household already has one by that name), 404 one the caller cannot see.
 *
 * Every merchant read is refreshed (the list's sharing state, the picker's pages and "Most used", the
 * brand reads under the same root). No transaction changes. Analytics: `merchant_edited`, bare, as any
 * other edit to the record sends it.
 */
export function useShareMerchant() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, householdId }: { id: string; householdId: string }) => {
      const { data, error, response } = await api.POST('/api/v1/merchants/{merchant_id}/share', {
        params: { path: { merchant_id: id } },
        body: { household_id: householdId },
      })
      if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
      return data
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.merchants.all() })
      void client.invalidateQueries({ queryKey: keys.merchantUsage.all() })
      emit('merchant_edited')
    },
    onError: (e) => {
      // A 409 can mean the merchant's sharing changed elsewhere: show what the server holds now.
      if (e instanceof MerchantsError && e.status === 409) void client.invalidateQueries({ queryKey: keys.merchants.all() })
    },
  })
}

/**
 * `DELETE /merchants/{id}/share`: stop sharing. Owner only (403); 409 if it is not shared.
 *
 * **Other members' transactions lose the merchant** — they become "No merchant"; the owner's own keep
 * it. Resolves to the number of links cleared (`cleared_transactions`), which the caller reports. There
 * is no preview of that count, so the confirm before it says what happens without one.
 *
 * Refreshes merchants AND transactions AND cashflow: the cleared rows change merchant, and the cashflow
 * merchant ranking and its "No merchant" remainder move with them.
 */
export function useUnshareMerchant() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<number> => {
      const { data, error, response } = await api.DELETE('/api/v1/merchants/{merchant_id}/share', {
        params: { path: { merchant_id: id } },
      })
      if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
      return data.cleared_transactions
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.merchants.all() })
      void client.invalidateQueries({ queryKey: keys.merchantUsage.all() })
      void client.invalidateQueries({ queryKey: keys.transactions.all() })
      void client.invalidateQueries({ queryKey: keys.cashflow.all() })
      emit('merchant_edited')
    },
    onError: (e) => {
      if (e instanceof MerchantsError && e.status === 409) void client.invalidateQueries({ queryKey: keys.merchants.all() })
    },
  })
}
